import { useEffect, useRef } from "react";
import clsx from "clsx";

export type ConfirmOptions = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm button uses the blush palette to signal a
   *  destructive action that can't be undone. */
  danger?: boolean;
};

type Props = ConfirmOptions & {
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Calm pastel confirmation dialog. Replaces the browser's native confirm()
 * for destructive actions where a Bin / soft-delete safety net isn't enough
 * (lanes, threads, permanent purges). For reversible deletes (cards) the
 * app shows a toast with Undo instead — no dialog at all.
 */
export function ConfirmDialog({
  title,
  body,
  confirmLabel = "yes, do it",
  cancelLabel = "cancel",
  danger = false,
  onCancel,
  onConfirm,
}: Props) {
  // Focus the cancel button by default so a stray Enter doesn't accidentally
  // confirm a destructive action.
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter") {
        // Enter on the (focused) cancel button cancels; this only fires the
        // confirm path if focus has been moved there explicitly.
        if (document.activeElement === cancelRef.current) return;
        e.preventDefault();
        onConfirm();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel, onConfirm]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-ink/10 px-4 pt-[20vh] backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-lane border border-border bg-surface shadow-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={clsx(
            "h-1 w-full",
            danger ? "bg-blush-200" : "bg-lavender-200"
          )}
        />
        <div className="px-7 pt-6 pb-5">
          <h2 className="text-lg font-semibold tracking-tight text-ink">
            {title}
          </h2>
          {body && (
            <p className="mt-2 text-sm leading-relaxed text-ink-soft">{body}</p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border bg-canvas/40 px-6 py-3">
          <button
            ref={cancelRef}
            onClick={onCancel}
            className="rounded-lg px-3 py-1.5 text-sm text-ink-soft transition-colors hover:bg-canvas hover:text-ink focus-ring"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={clsx(
              "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-ring",
              danger
                ? "bg-blush-100 text-blush-400 hover:bg-blush-200/70"
                : "bg-lavender-100 text-lavender-400 hover:bg-lavender-200/70"
            )}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
